// React and React Native Core imports
import { useMemo, useEffect } from 'react';

// 3rd party libraries
import { gql, useMutation, useQuery } from '@apollo/client';
import { useFormik, FormikProvider } from 'formik';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';

// Our Own imports
import { ComboboxSelfLoadingField, ComboboxField } from '../../atoms/Combobox';

// Relative path imports
import './AddBarnAccessForm.scss';

const GET_ROLES_QUERY = gql`
  query AddBarnAccessForm_GetRoles($barnId: uuid!) {
    roles(
      where: { _or: [{ organization: { farms: { id: { _eq: $barnId } } } }, { organization_id: { _is_null: true } }] }
    ) {
      id
      name
      privileges
    }
  }
`;

const UPSERT_USER_BARN_MUTATION = gql`
  mutation AddBarnAccessForm_UpsertUserBarn($object: user_barn_insert_input!) {
    insert_user_barn_one(object: $object, on_conflict: { constraint: user_barn_pkey, update_columns: [deleted_at] }) {
      user_id
      barn_id
      role_id
      deleted_at
    }
  }
`;

const handleSuccess = (barnName, roleName) =>
  toast.success(`Success! User can now access ${barnName} with role ${roleName}`, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

const handleError = (error) => {
  let errorMessage = 'An error has occurred while adding barn access. Please try again.';

  console.error('Error adding barn access:', error);

  if (error?.cause?.extensions?.code === 'constraint-violation' && error?.message?.includes('user_barn_pkey')) {
    // not sentry capturing this one as it's an expected error if someone selects something incorrectly
    errorMessage = `A user cannot be added with the same barn (${barnName}) + role ${roleName} more than once.`;
  } else {
    Sentry?.captureException(error);
  }
  toast.error(
    <div>
      <h4>Error</h4>
      <p>{errorMessage}</p>
    </div>,
    {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    },
  );
};

export default function AddBarnAccessForm({ userId, onSuccess = () => {}, userBarns = [] }) {
  // --- State hooks ---
  const formik = useFormik({
    initialValues: {
      barn_id: '',
      role_id: '',
      barn_name: '',
      role_name: '',
    },
    onSubmit: async (values, formikBag) => {
      if (!values.barn_id || !values.role_id) {
        toast.error('Please select both a barn and a role');
        return;
      }

      try {
        const { data } = await upsertUserBarn({
          variables: {
            object: {
              user_id: userId,
              barn_id: values.barn_id,
              role_id: values.role_id,
              deleted_at: null, // Ensure deleted_at is set to null for upsert
            },
          },
        });

        if (data?.insert_user_barn_one) {
          handleSuccess(values.barn_name, values.role_name);
          onSuccess(data.insert_user_barn_one);
          formikBag.resetForm();
        } else {
          handleError(data);
        }
      } catch (error) {
        console.error('Error adding barn access:', error);
        handleError(error);
      }
    },
  });

  // --- Apollo/GraphQL hooks ---
  const [upsertUserBarn, { loading: saving }] = useMutation(UPSERT_USER_BARN_MUTATION, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  const {
    data: rolesData,
    loading: rolesLoading,
    error: rolesError,
  } = useQuery(GET_ROLES_QUERY, {
    variables: { barnId: formik.values.barn_id },
    skip: !formik.values.barn_id,
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    if (rolesError) {
      console.error('Error loading roles:', rolesError);
      Sentry.captureException(rolesError);
      toast.error('Failed to load roles. Please try again.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [rolesError]);

  // --- Derived state ---
  const roles = rolesData?.roles || [];

  // Create a map of barn IDs to existing role IDs (only for active entries)
  const existingActiveBarnRoles = useMemo(() => {
    const barnRolesMap = {};
    userBarns.forEach((userBarn) => {
      if (userBarn.barn?.id && userBarn.role?.id && !userBarn.deleted_at) {
        if (!barnRolesMap[userBarn.barn.id]) {
          barnRolesMap[userBarn.barn.id] = [];
        }
        barnRolesMap[userBarn.barn.id].push(userBarn.role.id);
      }
    });
    return barnRolesMap;
  }, [userBarns]);

  // Get existing active role IDs for the selected barn
  const existingActiveRoleIds = existingActiveBarnRoles[formik.values.barn_id] || [];

  // Filter roles and add appropriate labels
  const availableRoles = roles.map((role) => {
    const isActive = existingActiveRoleIds.includes(role.id);

    return {
      ...role,
      disabled: isActive, // Only disable if already active
      name: isActive ? `${role.name} (Already active)` : role.name,
    };
  });

  return (
    <FormikProvider value={formik}>
      <form className="AddBarnAccessForm" onSubmit={formik.handleSubmit}>
        <div className="AddBarnAccessForm-row">
          <ComboboxSelfLoadingField
            name="barn_id"
            placeholder="Select Barn"
            label="Barn"
            disabled={formik.isSubmitting || saving}
            fieldTableName="farm"
            fieldHasuraName="name"
            className="AddBarnAccessForm-barnSelect"
            onChange={(selectedBarn) => {
              formik.setFieldValue('barn_id', selectedBarn?.id || '');
              formik.setFieldValue('barn_name', selectedBarn?.name || '');
              // Clear role when barn changes
              formik.setFieldValue('role_id', '');
              formik.setFieldValue('role_name', '');
            }}
          />

          <ComboboxField
            disabled={formik.isSubmitting || saving || rolesLoading}
            name="role_id"
            placeholder={rolesLoading ? 'Loading roles...' : 'Select Role'}
            label="Role"
            className="AddBarnAccessForm-roleSelect"
            onChange={(selectedRole) => {
              formik.setFieldValue('role_id', selectedRole?.id || '');
              formik.setFieldValue('role_name', selectedRole?.name || '');
              if (selectedRole?.id && selectedRole?.name && formik?.values?.barn_id && formik?.values?.barn_name) {
                formik?.submitForm();
              }
            }}
            items={availableRoles.map((role) => ({
              id: role.id,
              name: role.name,
              value: role.id,
              disabled: role.disabled,
              listItemClassName: role.disabled ? 'Combobox-listItem-disabledText' : '',
            }))}
          />
        </div>
      </form>
    </FormikProvider>
  );
}

AddBarnAccessForm.propTypes = {
  userId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  userBarns: PropTypes.array,
};
